import classnames from 'classnames';
import { useState } from 'react';

import { get, startCase } from 'lodash';

import { useSelectedAccount } from '@/lib/accounts/context';
import { TextArea } from '@/components/form/text-area';
import { Button } from '@/components/button';
import { useToastContext } from '@/components/toast';
import {
  useUpdateAiBusinessConceptsMutation,
  useUpsertAiSystemInstructionMutation,
} from '@/graphql';

import { Icon } from '@/components/icon';

import { Section } from '../common';

import { getIntegrationIcon } from '../integrations/utils';

import formStyles from '../../components/form/form.module.scss';
import styles from './styles.module.scss';

const connectionTypeMap = {
  bigquery: 'BigQuery',
  postgres: 'PostgreSQL',
  mysql: 'MySQL',
  redshift: 'Redshift',
  snowflake: 'Snowflake',
  databricks: 'Databricks',
};

interface InstructionCardProps {
  title: string;
  description: string;
  initialInstructions: string;
  integrationId?: string;
  connectionId?: string;
  icon?: React.ReactNode;
}

const InstructionCard = ({
  title,
  description,
  initialInstructions,
  integrationId,
  connectionId,
  icon,
}: InstructionCardProps) => {
  const account = useSelectedAccount();
  const toast = useToastContext();
  const [instructions, setInstructions] = useState(initialInstructions);
  const [upsertAiSystemInstruction, { loading }] = useUpsertAiSystemInstructionMutation();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      const instructionsValue = instructions.trim() === '' ? null : instructions;
      await upsertAiSystemInstruction({
        variables: {
          accountId: account.accountId,
          integrationId: integrationId ?? null,
          connectionId: connectionId ?? null,
          instructions: instructionsValue,
        },
        refetchQueries: ['AccountSettings'],
      });

      toast({
        kind: 'success',
        title: 'Update complete!',
        content: () => 'Instructions have been updated.',
      });
    } catch (error) {
      toast({
        kind: 'error',
        title: 'Update failed',
        content: () => 'Failed to update instructions. Please try again.',
      });
      console.error('Failed to update instructions:', error);
    }
  };

  const hasChanges = instructions !== initialInstructions;

  return (
    <div className={styles.card}>
      <form onSubmit={handleSubmit} className={classnames(formStyles.formHorizontal)}>
        <h3>
          {icon}
          {title}
        </h3>
        <p>{description}</p>
        <div className={formStyles.formRow}>
          <TextArea
            placeholder={`Instructions specific to ${title}...`}
            value={instructions}
            rows={4}
            onChange={(e) => setInstructions(e.target.value)}
          />
        </div>
        <div>
          <Button type="submit" loading={loading} disabled={!hasChanges}>
            Save
          </Button>
        </div>
      </form>
    </div>
  );
};

interface AiBusinessConceptsSettingsProps {
  aiBusinessConcepts: string;
  accountName: string | undefined;
  integrations: {
    integrationId: string;
    name?: string | null;
    service: string;
  }[];
  connections: {
    connectionId: string;
    name: string;
    type: string;
  }[];
  aiSystemInstructions: {
    integrationId?: string | null;
    connectionId?: string | null;
    instructions: string;
  }[];
}

export const AiBusinessConceptsSettings = ({
  aiBusinessConcepts,
  accountName,
  integrations,
  connections,
  aiSystemInstructions,
}: AiBusinessConceptsSettingsProps) => {
  const account = useSelectedAccount();
  const [generalInstructions, setGeneralInstructions] = useState(aiBusinessConcepts);
  const [updateGeneralInstructions, { loading: loadingUpdateGeneralInstructions }] =
    useUpdateAiBusinessConceptsMutation();
  const toast = useToastContext();

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    updateGeneralInstructions({
      variables: {
        accountId: account.accountId,
        aiBusinessConcepts: generalInstructions,
      },
      refetchQueries: ['AccountSettings'],
    });

    toast({
      kind: 'success',
      title: 'Update complete!',
      content: () => 'Account business concepts has been updated.',
    });
  };

  const placeholder = [
    `${accountName} is a company that...`,
    '',
    'Here are some key business concepts:\n- one\n- two',
  ].join('\n');

  return (
    <Section title="AI Business Concepts">
      <p className={styles.introInstructions}>
        Providing custom business concepts and definitions helps Supersimple&apos;s AI better
        understand your business and data: what things mean and which searches to run.
      </p>
      <p className={styles.introInstructions}>
        High-quality instructions help improve both the speed and accuracy of AI responses.
      </p>
      <div className={styles.card}>
        <form onSubmit={handleFormSubmit} className={classnames(formStyles.formHorizontal)}>
          <h3>General instructions</h3>
          <p>
            These apply to every connected data source. It&apos;s a good place to share context
            about your company, product, and internal terminology.
          </p>
          <div className={formStyles.formRow}>
            <TextArea
              placeholder={placeholder}
              value={generalInstructions}
              rows={5}
              onChange={(e) => setGeneralInstructions(e.target.value)}
            />
          </div>

          <div>
            <Button
              type="submit"
              loading={loadingUpdateGeneralInstructions}
              disabled={aiBusinessConcepts === generalInstructions}>
              Save
            </Button>
          </div>
        </form>
      </div>

      {connections.map((connection) => {
        const connectionTypeName = get(connectionTypeMap, connection.type, connection.type);
        const initialInstructions =
          aiSystemInstructions.find(
            (item) =>
              item.connectionId === connection.connectionId &&
              (item.integrationId === null || item.integrationId === undefined),
          )?.instructions ?? '';

        return (
          <InstructionCard
            key={connection.connectionId}
            title={connectionTypeName}
            description={`Provide specific instructions for data and data models specific to ${connectionTypeName}.`}
            initialInstructions={initialInstructions}
            connectionId={connection.connectionId}
          />
        );
      })}

      {integrations.map((integration) => {
        const icon = getIntegrationIcon(integration.service);
        const integrationTypeName = startCase(integration.service.replace(/_/g, ' ').toLowerCase());
        const initialInstructions =
          aiSystemInstructions.find(
            (item) =>
              item.integrationId === integration.integrationId &&
              (item.connectionId === null || item.connectionId === undefined),
          )?.instructions ?? '';

        const title =
          integration.name !== null && integration.name !== undefined
            ? `${integrationTypeName} (${integration.name})`
            : integrationTypeName;
        const iconElement =
          icon !== undefined ? (
            <div className={styles.icon}>
              <Icon name={icon} size={20} />
            </div>
          ) : undefined;

        return (
          <InstructionCard
            key={integration.integrationId}
            title={title}
            description={`Provide specific instructions for understanding and finding data from ${integrationTypeName}.`}
            initialInstructions={initialInstructions}
            integrationId={integration.integrationId}
            icon={iconElement}
          />
        );
      })}
    </Section>
  );
};
